import { Menu, MenuItem, Divider, ListItemIcon, ListItemText, Typography } from "@mui/material"
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material"
import { useTranslation } from "react-i18next"
import {getAllowedOrderStatusTransitions} from "../../components/orders/order-status-validator"
import {ORDER_STATUS_APPEARANCE} from "../../components/orders/order-status-control"

const OrderStatusMenu = ({ anchorEl, open, onClose, order, orderStatuses, onStatusSelect }) => {
    const { t } = useTranslation()

    const allowedTransitions = getAllowedOrderStatusTransitions(order)

    return (
        <Menu anchorEl={anchorEl} open={open} onClose={onClose}
            MenuListProps={{id: "order-status-menu", "aria-labelledby": anchorEl?.id}}>
            <MenuItem disabled sx={{ fontWeight: "bold", color: "primary.main" }}>
                {t("ChangeStatus")}
            </MenuItem>
            <Divider />

            {/* Show current status info if it's a final state */}
            {allowedTransitions.length === 0 && [
                    <MenuItem key="no-changes" disabled sx={{ fontStyle: "italic", color: "text.secondary" }}>
                        {t("NoStatusChangesAllowed")}
                    </MenuItem>,
                    <Divider key="no-changes-divider" />
            ]}

            {Object.entries(orderStatuses).map(([key, status]) => {
                const isCurrentStatus = order?.status?.toLowerCase() === key.toLowerCase()
                const isAllowed = allowedTransitions.includes(key.toLowerCase())
                const isDisabled = isCurrentStatus || (!isAllowed && !isCurrentStatus)
                const appearance = ORDER_STATUS_APPEARANCE[key.toUpperCase()]
                const Icon = appearance?.Icon

                return (
                    <MenuItem
                        key={key}
                        onClick={() => onStatusSelect(key)}
                        disabled={isDisabled}
                        sx={{
                            "&:hover": {
                                bgcolor: isDisabled ? "transparent" : appearance?.background,
                            },
                            opacity: isDisabled ? 0.5 : 1,
                            position: "relative",
                        }}
                    >
                        <ListItemIcon sx={{color: appearance?.foreground}}>{Icon ? <Icon fontSize="small"/> : status.icon}</ListItemIcon>
                        <ListItemText>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: isCurrentStatus ? "primary.main" : "inherit",
                                    fontWeight: isCurrentStatus ? "medium" : "normal",
                                }}
                            >
                                {t(status.label)}
                                {isCurrentStatus && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        ({t("Current")})
                                    </Typography>
                                )}
                                {!isAllowed && !isCurrentStatus && (
                                    <Typography component="span" variant="caption" color="error.main" sx={{ ml: 1, fontSize: "0.7rem" }}>
                                        ({t("NotAllowed")})
                                    </Typography>
                                )}
                            </Typography>
                        </ListItemText>
                        {isCurrentStatus && <CheckCircleIcon sx={{ ml: 1, color: "success.main", fontSize: 16 }} />}
                    </MenuItem>
                )
            })}

            {/* Show allowed transitions info */}
            {allowedTransitions.length > 0 && [
                    <Divider key="allowed-divider" />,
                    <MenuItem key="allowed" disabled sx={{ fontStyle: "italic", color: "text.secondary", fontSize: "0.8rem" }}>
                        {t("AllowedTransitions")}:{" "}
                        {allowedTransitions.map((status) => t(orderStatuses[status]?.label || status)).join(", ")}
                    </MenuItem>
            ]}
        </Menu>
    )
}

export default OrderStatusMenu

